/*
  Kiwi Ears · article navigation (Rene 2026-08-22).

  Four small jobs on a long read, all driven from the prose that is already on
  the page rather than from anything the merchandiser has to maintain:

    1. give every heading a stable id and a link to itself
    2. build the contents list from those headings
    3. mark the section being read, in the list and in the URL
    4. a progress bar, and the share row's copy-link button

  The contents list is built from the DOM, not from Liquid: the body is rich
  text out of Shopify's editor, and Liquid cannot parse HTML without string
  hacks that break the first time someone pastes a heading with an attribute.

  Everything here is progressive. With no JS the article still reads, the
  headings still have their text, and the contents block is not in the markup at
  all — it is created here, so it can never render empty.

  ScrollTrigger rather than IntersectionObserver, per CLAUDE.md: it stays in step
  with Lenis. Reduced motion gets the same navigation with no smooth scrolling
  and no bar animation.
*/
(function () {
  if (window.__kiwiArticleReady) return;
  window.__kiwiArticleReady = true;

  var article = document.querySelector('[data-article-body]');
  if (!article) return;

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- headings */

  /* Only h2 and h3. A contents list that also carries h4s stops being a summary
     and becomes a second copy of the article. */
  var headings = [].slice.call(article.querySelectorAll('h2, h3'))
    .filter(function (h) { return h.textContent.trim().length > 1; });

  var used = Object.create(null);
  function slug(text, i) {
    var base = text.toLowerCase().trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60) || ('section-' + (i + 1));
    /* Two headings can share a title — "Verdict" twice in a comparison piece.
       Without deduping, both anchors point at the first one. */
    var id = base, n = 2;
    while (used[id] || (document.getElementById(id) && document.getElementById(id) !== null)) {
      id = base + '-' + n++;
    }
    used[id] = true;
    return id;
  }

  headings.forEach(function (h, i) {
    if (!h.id) h.id = slug(h.textContent, i);
    /* scroll-margin so a heading jumped to does not hide under the fixed bar */
    h.style.scrollMarginTop = '120px';

    if (h.querySelector('.kh-anchor')) return;
    var a = document.createElement('a');
    a.className = 'kh-anchor';
    a.href = '#' + h.id;
    a.setAttribute('aria-label', 'Link to this section: ' + h.textContent.trim());
    a.innerHTML = '<span aria-hidden="true">#</span>';
    h.appendChild(a);
  });

  /* ----------------------------------------------------------------- scroll */

  function goTo(el) {
    if (window.lenis && !still) {
      window.lenis.scrollTo(el, { offset: -110 });
    } else {
      var y = el.getBoundingClientRect().top + window.pageYOffset - 110;
      window.scrollTo({ top: y, behavior: still ? 'auto' : 'smooth' });
    }
  }

  /* ----------------------------------------------------------------- contents */

  var tocMount = document.querySelector('[data-toc]');
  var links = [];

  /* Under two headings there is nothing to navigate — the list would be furniture.
     The mount stays empty and its CSS keeps it out of the layout. */
  if (tocMount && headings.length >= 2) {
    var list = document.createElement('ol');
    list.className = 'kh-toc-list';

    headings.forEach(function (h) {
      var li = document.createElement('li');
      li.className = 'kh-toc-item' + (h.tagName === 'H3' ? ' kh-toc-item--sub' : '');
      var a = document.createElement('a');
      a.className = 'kh-toc-link';
      a.href = '#' + h.id;
      a.textContent = h.textContent.replace(/#$/, '').trim();
      a.addEventListener('click', function (e) {
        e.preventDefault();
        goTo(h);
        /* The reader can share where they are. replaceState, not pushState —
           a contents list should not fill the back button with the same page. */
        history.replaceState(null, '', '#' + h.id);
      });
      li.appendChild(a);
      list.appendChild(li);
      links.push({ a: a, h: h });
    });

    tocMount.appendChild(list);
    tocMount.setAttribute('data-toc-ready', '');

    /* On a phone the list is a lid, and it starts shut: six entries above the
       first paragraph push the article most of a screen down. On desktop it is
       a rail beside the prose, so it stays open. The `open` attribute is in the
       markup rather than added here, so a phone with no JS gets the list open —
       usable, just longer — instead of a heading with nothing under it. */
    var box = tocMount.closest('details');
    if (box && window.matchMedia && window.matchMedia('(max-width: 1023px)').matches) {
      box.removeAttribute('open');
    }
  }

  /* Anchor links in the prose scroll rather than jump, for the same reason. */
  article.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a.kh-anchor');
    if (!a) return;
    e.preventDefault();
    var el = document.getElementById(a.getAttribute('href').slice(1));
    if (!el) return;
    goTo(el);
    history.replaceState(null, '', a.getAttribute('href'));
  });

  /* ------------------------------------------------------------------- arming */

  function markActive(idx) {
    links.forEach(function (l, i) {
      if (i === idx) {
        l.a.setAttribute('aria-current', 'true');
      } else {
        l.a.removeAttribute('aria-current');
      }
    });
  }

  function arm() {
    var g = window.gsap, ST = window.ScrollTrigger;

    /* progress bar — width, not transform, is fine here: it is one element and
       the alternative (scaleX) blurs a 3px bar's leading edge in Safari */
    var bar = document.querySelector('[data-read-progress]');
    if (bar && ST && g) {
      var paint = function (self) {
        bar.style.width = (self.progress * 100).toFixed(2) + '%';
      };
      ST.create({
        trigger: article,
        start: 'top 140px',
        end: 'bottom bottom',
        onUpdate: paint,
        /* onUpdate alone leaves the bar unset until the first scroll, so a page
           opened at an anchor part-way down shows an empty bar over a reader who
           is already halfway. onRefresh fires at creation and on resize. */
        onRefresh: paint
      });
    } else if (bar) {
      bar.remove();
    }

    if (!links.length) return;

    if (!ST || !g) {
      /* No GSAP: the list still navigates, it just does not follow along. */
      markActive(0);
      return;
    }

    /* A section runs from its own heading reaching the bar to the NEXT heading
       reaching it — so endTrigger is the next heading, not an offset. Computing
       the end from offsetTop instead looks equivalent and is not: a function
       `end` must return an absolute scroll position, so the arithmetic version
       marked the first heading and never advanced. The last section ends with
       the article. */
    links.forEach(function (l, i) {
      var next = links[i + 1];
      ST.create({
        trigger: l.h,
        start: 'top 140px',
        endTrigger: next ? next.h : article,
        end: next ? 'top 140px' : 'bottom 140px',
        onToggle: function (self) { if (self.isActive) markActive(i); }
      });
    });

    /* onToggle only fires on a CHANGE, so a page loaded part-way down — a
       refresh, or a link straight to #verdict — would show nothing marked
       until the reader scrolled. Same initial-sync trap as the nav theme. */
    ST.refresh();
    var y = window.pageYOffset + 141;
    var current = 0;
    links.forEach(function (l, i) {
      if (l.h.getBoundingClientRect().top + window.pageYOffset <= y) current = i;
    });
    markActive(current);
  }

  /* ------------------------------------------------------------------- share */

  var copy = document.querySelector('[data-share-copy]');
  if (copy) {
    var label = copy.querySelector('[data-share-copy-label]');
    var original = label ? label.textContent : '';
    copy.addEventListener('click', function () {
      var url = window.location.href.split('#')[0];
      var done = function () {
        if (!label) return;
        label.textContent = copy.getAttribute('data-copied') || 'Link copied';
        copy.setAttribute('data-state', 'done');
        setTimeout(function () {
          label.textContent = original;
          copy.removeAttribute('data-state');
        }, 2200);
      };
      /* navigator.clipboard needs a secure context; the fallback keeps the
         button honest on http previews rather than silently doing nothing. */
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(done, function () {});
      } else {
        var t = document.createElement('textarea');
        t.value = url;
        t.setAttribute('readonly', '');
        t.style.cssText = 'position:fixed;top:-9999px';
        document.body.appendChild(t);
        t.select();
        try { document.execCommand('copy'); done(); } catch (err) {}
        document.body.removeChild(t);
      }
    });
  }

  /* The native sheet is better than three brand links on a phone, but it only
     exists on some browsers — so the button is created here rather than sitting
     in the markup being useless.

     Coarse pointers only. Desktop Chrome has navigator.share too, but there the
     sheet is a worse answer than the links already on screen, and adding it
     makes five buttons where four will do. */
  var touch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  var shareRow = document.querySelector('[data-share-row]');
  if (shareRow && navigator.share && touch) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kh-share-btn';
    btn.innerHTML = '<span>' + (shareRow.getAttribute('data-share-label') || 'Share') + '</span>';
    btn.addEventListener('click', function () {
      navigator.share({
        title: document.title,
        url: window.location.href.split('#')[0]
      }).catch(function () {});
    });
    shareRow.insertBefore(btn, shareRow.firstChild);
  }

  /* ------------------------------------------------------- product blocks */

  /* Quick-add is a single delegated handler now (assets/kiwi-quickadd.js,
     Rene 2026-09-04) — it matches .kh-padd as well as .pk-padd and keeps the
     label swap these buttons rely on. This file's own copy would have added
     twice, since the rail and block buttons carry both classes. */

  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm);
})();
